import React, { useEffect, useRef, useState } from "react";
import { Send, Loader2, Sparkles, Coins, Settings2, CalendarClock, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import StreakBadge from "@/components/StreakBadge";

const PERSONALITY_LABELS = {
  mogul_voice: "Signature Voice",
  glasgow_coach: "Glasgow Coach",
  strategic_operator: "Strategic Operator",
  brutal_growth_hacker: "Brutal Growth Hacker",
};

const WingmanPanel = ({ user, refreshUser }) => {
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pendingAction, setPendingAction] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [personality, setPersonality] = useState(
    user?.wingman_personality || "mogul_voice"
  );
  const [personalities, setPersonalities] = useState([]);
  const scrollRef = useRef(null);

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/wingman/threads");
      setThreads(data || []);
    } catch (e) {}
  };

  useEffect(() => {
    loadThreads();
    api.get("/wingman/personalities").then((r) => setPersonalities(r.data || []));
  }, []);

  useEffect(() => {
    if (activeThread) {
      api.get(`/wingman/threads/${activeThread}`).then((r) => setMessages(r.data || []));
    } else {
      setMessages([]);
    }
  }, [activeThread]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const setPersonalityRemote = async (id) => {
    setPersonality(id);
    try {
      await api.post("/wingman/personality", { personality: id });
      toast.success(`Tam is now: ${PERSONALITY_LABELS[id] || id}`);
    } catch {}
  };

  const send = async () => {
    if (!draft.trim()) return;
    if (user?.role !== "admin" && (user?.wingman_tokens || 0) < 5) {
      toast.error("Out of Tam tokens. Top up on the pricing page.");
      return;
    }
    setSending(true);
    const text = draft;
    setDraft("");
    setMessages((m) => [
      ...m,
      { role: "user", content: text, created_at: new Date().toISOString() },
    ]);
    try {
      const { data } = await api.post("/wingman/message", {
        text,
        thread_id: activeThread,
        personality,
      });
      setActiveThread(data.thread_id);
      setMessages((m) => [
        ...m,
        { role: "assistant", content: data.reply, created_at: new Date().toISOString() },
      ]);
      if (data.action) setPendingAction(data.action);
      // streak milestone toast
      if (data.streak?.milestone_hit) {
        toast.success(
          `🔥 ${data.streak.milestone_hit}-day streak! +${data.streak.bonus_tokens} Tam tokens.`
        );
      }
      refreshUser?.();
      loadThreads();
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("You've been signed out. Please sign back in — your message is kept below.");
      } else {
        toast.error(e?.response?.data?.detail || "Tam could not reply.");
      }
      // Keep what the user typed and drop the optimistic user bubble.
      setDraft(text);
      setMessages((m) => m.filter((x) => !(x.role === "user" && x.content === text)));
    } finally {
      setSending(false);
    }
  };

  const confirmAction = async () => {
    if (!pendingAction) return;
    setConfirming(true);
    try {
      if (pendingAction.kind === "control") {
        const { data } = await api.post("/wingman/control", pendingAction.control);
        toast.success(data.message || "Done.");
        setMessages((m) => [...m, { role: "assistant", content: `✅ ${data.message || "Done"} — ${pendingAction.summary}`, created_at: new Date().toISOString() }]);
      } else {
        const { data } = await api.post("/wingman/confirm-action", { automation: pendingAction.automation });
        toast.success("Scheduled! It'll run itself from now on.");
        setMessages((m) => [...m, { role: "assistant", content: `✅ Done — ${data.summary}`, created_at: new Date().toISOString() }]);
      }
      setPendingAction(null);
      loadThreads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't do that.");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="wingman-panel">
      {/* sidebar threads */}
      <aside className="lg:col-span-3 bg-[#0A192F] border border-white/10 rounded-sm p-4 max-h-[70vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400">
            Threads
          </p>
          <button
            onClick={() => {
              setActiveThread(null);
              setMessages([]);
            }}
            data-testid="wingman-new-thread"
            className="text-xs text-cyan-300 hover:text-white"
          >
            + New
          </button>
        </div>
        <ul className="space-y-1">
          {threads.length === 0 && (
            <p className="text-slate-500 text-xs">No conversations yet.</p>
          )}
          {threads.map((t) => (
            <li key={t.thread_id}>
              <button
                onClick={() => setActiveThread(t.thread_id)}
                data-testid={`wingman-thread-${t.thread_id}`}
                className={[
                  "w-full text-left px-3 py-2 rounded-sm text-xs",
                  activeThread === t.thread_id
                    ? "bg-cyan-400/10 text-white border border-cyan-400/30"
                    : "text-slate-300 hover:bg-white/5",
                ].join(" ")}
              >
                <div className="truncate">{t.title}</div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mt-1">
                  {PERSONALITY_LABELS[t.personality] || t.personality}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      {/* chat */}
      <div className="lg:col-span-9 bg-[#0A192F] border border-white/10 rounded-sm flex flex-col" style={{ height: "70vh" }}>
        <header className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Sparkles size={16} className="text-cyan-300" />
            <div>
              <p className="text-white font-heading font-bold">Tam</p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                {PERSONALITY_LABELS[personality]}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <StreakBadge streak={user?.streak} compact testIdPrefix="wingman-streak" />
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#050B14] border border-white/10 rounded-sm">
              <Coins size={14} className="text-cyan-300" />
              <span className="font-mono text-xs text-white" data-testid="wingman-token-balance">
                {user?.role === "admin" ? "∞" : (user?.wingman_tokens ?? 0)}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-slate-500">
                {user?.role === "admin" ? "unlimited" : "tokens"}
              </span>
            </div>
            <details className="relative">
              <summary className="list-none cursor-pointer text-slate-300 hover:text-white" data-testid="wingman-personality-toggle">
                <Settings2 size={16} />
              </summary>
              <div className="absolute right-0 mt-2 w-56 bg-[#050B14] border border-white/10 p-2 z-10">
                {personalities.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setPersonalityRemote(p.id)}
                    data-testid={`wingman-set-personality-${p.id}`}
                    className={[
                      "w-full text-left px-3 py-2 text-xs rounded-sm",
                      personality === p.id
                        ? "bg-cyan-400/10 text-white"
                        : "text-slate-300 hover:bg-white/5",
                    ].join(" ")}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </details>
          </div>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="wingman-messages">
          {messages.length === 0 && (
            <div className="h-full flex items-center justify-center text-center text-slate-500 text-sm">
              <div>
                <p>Right, what we doing today?</p>
                <p className="mt-1 text-xs font-mono text-slate-600">{user?.role === "admin" ? "Ask anything. Unlimited replies." : "Ask anything. 5 tokens per reply."}</p>
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div
              key={i}
              data-testid={`wingman-message-${m.role}-${i}`}
              className={[
                "max-w-[85%] px-4 py-3 rounded-sm text-sm whitespace-pre-wrap",
                m.role === "user"
                  ? "ml-auto bg-cyan-400/10 border border-cyan-400/30 text-white"
                  : "bg-[#050B14] border border-white/10 text-slate-200",
              ].join(" ")}
            >
              {m.content}
            </div>
          ))}
          {sending && (
            <div className="bg-[#050B14] border border-white/10 px-4 py-3 max-w-[85%] text-slate-500 text-sm">
              Tam is thinking…
            </div>
          )}
          {pendingAction && (
            <div className="bg-[#0A192F] border border-cyan-400/40 rounded-sm p-4 max-w-[85%]" data-testid="wingman-action-confirm">
              <p className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 flex items-center gap-1">
                <CalendarClock size={12} /> {pendingAction.kind === "control" ? "Confirm this change" : "Confirm this automation"}
              </p>
              <p className="text-sm text-white mt-1 capitalize">{pendingAction.summary}</p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={confirmAction}
                  disabled={confirming}
                  data-testid="wingman-action-confirm-btn"
                  className="inline-flex items-center gap-1 px-4 py-2 bg-[#00F0FF] text-[#050B14] font-bold rounded-sm text-[11px] uppercase tracking-wider hover:bg-[#33F3FF] disabled:opacity-50"
                >
                  {confirming ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} {pendingAction.kind === "control" ? "Confirm" : "Confirm & schedule"}
                </button>
                <button
                  onClick={() => setPendingAction(null)}
                  data-testid="wingman-action-dismiss-btn"
                  className="px-4 py-2 text-slate-400 hover:text-white text-[11px] uppercase tracking-wider"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-white/10 flex gap-2">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !sending && send()}
            placeholder="Ask Tam…"
            data-testid="wingman-input"
            className="flex-1 px-4 py-3 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white placeholder-slate-500 text-sm rounded-sm"
          />
          <button
            onClick={send}
            disabled={sending || !draft.trim()}
            data-testid="wingman-send"
            className="px-4 py-3 bg-[#00F0FF] text-[#050B14] font-bold text-sm rounded-sm hover:bg-[#33F3FF] disabled:opacity-60"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WingmanPanel;
